/**
 * Keyset paging, accumulated.
 *
 * THE PAGER CANNOT BE NUMBERED AND THERE IS NO "LAST PAGE". shared.yaml's `Page` offers a cursor
 * and nothing else — offset paging exists nowhere on this platform — so there is no page count, no
 * "page 3 of 7", and no way to jump. What there is: a next cursor, or null meaning this was the
 * last page. So the shape is LOAD MORE, and this hook is the accumulator behind it.
 *
 * WHY ACCUMULATE RATHER THAN REPLACE. Prev/Next over a keyset stream needs the caller to remember
 * every cursor it has seen in order to go backwards, and the cabinet's lists are short — one
 * operator, ~530 authors, a few dozen articles each. "Load more" needs one cursor and no history,
 * and it never shows a reader a page that has silently shifted underneath them.
 *
 * `next_cursor` ABSENT OR NULL BOTH MEAN THE LAST PAGE — the schema says "Absent or null means the
 * last page", so both are checked. Treating only null as the end leaves a list that never ends.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeysetPage } from '../api';

export type KeysetStatus = 'loading' | 'ready' | 'error';

export interface KeysetList<TItem, TPage extends KeysetPage<TItem> = KeysetPage<TItem>> {
  readonly items: readonly TItem[];
  /**
   * The most recent RESPONSE, envelope and all. Several of these pages carry more than their
   * items — `ArticleStatsPage` carries the `from`/`to` the server resolved, `ArticleListPage`
   * carries `total` — and a screen that only got the items back would have to guess at both.
   */
  readonly lastPage: TPage | undefined;
  readonly status: KeysetStatus;
  readonly error: unknown;
  /** True while the FIRST page of the current key is loading and nothing is on screen yet. */
  readonly isInitialLoad: boolean;
  /** True while a further page is loading under rows that are already drawn. */
  readonly isLoadingMore: boolean;
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  /** Throws the accumulated pages away and reads the first page again. */
  readonly reload: () => void;
}

interface Request {
  readonly key: string;
  readonly cursor: string | undefined;
  readonly attempt: number;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useKeysetList<TItem, TPage extends KeysetPage<TItem> = KeysetPage<TItem>>(
  read: (cursor: string | undefined, signal: AbortSignal) => Promise<TPage>,
  key: string,
): KeysetList<TItem, TPage> {
  const [items, setItems] = useState<readonly TItem[]>([]);
  const [lastPage, setLastPage] = useState<TPage | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState<KeysetStatus>('loading');
  const [error, setError] = useState<unknown>(undefined);
  const [request, setRequest] = useState<Request>({ key, cursor: undefined, attempt: 0 });

  const readRef = useRef(read);
  readRef.current = read;

  // A KEY CHANGE IS A DIFFERENT QUESTION, so the answers so far are thrown away rather than
  // appended to — otherwise switching a filter grows one list out of two different queries.
  //
  // Adjusting during render rather than in an effect is React's own documented shape for this. It
  // re-renders immediately without committing, so the reader never sees a frame of the old list
  // under the new filter, and the first page is never fetched twice on mount.
  if (request.key !== key) {
    setRequest({ key, cursor: undefined, attempt: 0 });
    setItems([]);
    setLastPage(undefined);
    setCursor(undefined);
    setHasMore(false);
    setStatus('loading');
    setError(undefined);
  }

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setStatus('loading');
    setError(undefined);

    readRef
      .current(request.cursor, controller.signal)
      .then((response) => {
        if (!live) {
          return;
        }
        const received = response.items ?? [];
        setItems((current) =>
          request.cursor === undefined ? received : [...current, ...received],
        );
        setLastPage(response);
        const next = response.next_cursor;
        setCursor(next ?? undefined);
        setHasMore(next !== undefined && next !== null && next !== '');
        setStatus('ready');
      })
      .catch((cause: unknown) => {
        if (!live || isAbort(cause)) {
          return;
        }
        setError(cause);
        setStatus('error');
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [request]);

  const loadMore = useCallback(() => {
    if (cursor === undefined) {
      return;
    }
    setRequest((current) => ({ key: current.key, cursor, attempt: current.attempt }));
  }, [cursor]);

  const reload = useCallback(() => {
    setItems([]);
    setLastPage(undefined);
    setCursor(undefined);
    setHasMore(false);
    setRequest((current) => ({
      key: current.key,
      cursor: undefined,
      attempt: current.attempt + 1,
    }));
  }, []);

  return {
    items,
    lastPage,
    status,
    error,
    isInitialLoad: status === 'loading' && items.length === 0,
    isLoadingMore: status === 'loading' && items.length > 0,
    hasMore,
    loadMore,
    reload,
  };
}
